import { HubFeature } from '@ats/api/cm';
import { LocDefs, MapLocKeys } from '@cm/core/localization';
import { candExamsDPRoot } from '@cm/feature/exam';

const dpRoot = `${candExamsDPRoot}|Candidate Exam Items`;

export type SPCandidateExamItemsLoc =
	| 'SPCandidateExamItemsMaxScore'
	| 'SPCandidateExamItemsComments'
	| 'SPCandidateExamItemsPageTitle'
	| 'SPCandidateExamItemsName'
	| 'SPCandidateExamItemsScore'
	| 'SPCandidateExamItemsScoredStatus'
	| 'SPCandidateExamItemsTimeSpent'
	| 'SPCandidateExamItemsTimeSpentTotal'
	| 'SPCandidateExamItemsStatus'
	| 'SPCandidateExamItemsOrderPresented'
	| 'SPCandidateExamItemsSection'
	| 'SPCandidateExamItemsDistractorCount'
	| 'SPCandidateExamItemsResponseType'
	| 'SPCandidateExamItemsResponse'
	| 'SPCandidateExamItemsKey'
	| 'SPCandidateExamItemsMaxPointsPossible'
	| 'SPCandidateExamItemsCaseStudy'
	| 'SPCandidateExamItemsObUsed'
	| 'SPCandidateExamItemsObFirst'
	| 'SPCandidateExamItemsObItemTime'
	| 'SPCandidateExamItemsObPages'
	| 'SPCandidateExamItemsSummaryRowLabel';

export const spCandidateExamItemsLocs: LocDefs<SPCandidateExamItemsLoc> = {
	SPCandidateExamItemsMaxScore: {
		defaultValue: 'Max Score',
		description: 'Max score column heading for the exam items page.',
		displayPath: `${dpRoot}|Max Score`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
	},
	SPCandidateExamItemsComments: {
		defaultValue: 'Comments',
		description: 'Comments column heading for the exam items page.',
		displayPath: `${dpRoot}|Comments`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
	},
	SPCandidateExamItemsPageTitle: {
		defaultValue: 'Items',
		description: 'The title text for the candidate exam items page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.Title.Items',
	},
	SPCandidateExamItemsName: {
		defaultValue: 'Name',
		description: 'The name column heading for the exam items page.',
		displayPath: `${dpRoot}|Name`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.Name',
	},
	SPCandidateExamItemsScore: {
		defaultValue: 'Score',
		description: 'The score column heading for the exam items page.',
		displayPath: `${dpRoot}|Score`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.Score',
	},
	SPCandidateExamItemsScoredStatus: {
		defaultValue: 'Scored Status',
		description: 'The scored status column heading for the exam items page.',
		displayPath: `${dpRoot}|Scored Status`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.ScoredStatus',
	},
	SPCandidateExamItemsTimeSpent: {
		defaultValue: 'Time Spent',
		description: 'The time spent column heading for the exam items page.',
		displayPath: `${dpRoot}|Time Spent`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.TimeSpent',
	},
	SPCandidateExamItemsTimeSpentTotal: {
		defaultValue: 'Total Time Spent',
		description: 'The field label for the total time spent.',
		displayPath: `${dpRoot}|Time Spent Total`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
	},
	SPCandidateExamItemsStatus: {
		defaultValue: 'Status',
		description: 'The status column heading for the exam items page.',
		displayPath: `${dpRoot}|Status`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.Status',
	},
	SPCandidateExamItemsOrderPresented: {
		defaultValue: 'Order Presented',
		description: 'The order presented column heading for the exam items page.',
		displayPath: `${dpRoot}|Order Presented`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.OrderPresented',
	},
	SPCandidateExamItemsSection: {
		defaultValue: 'Section',
		description: 'The section column heading for the exam items page.',
		displayPath: `${dpRoot}|Section`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.Section',
	},
	SPCandidateExamItemsDistractorCount: {
		defaultValue: 'Distractor Count',
		description: 'The distractor count column heading for the exam items page.',
		displayPath: `${dpRoot}|Distractor Count`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.DistractorCount',
	},
	SPCandidateExamItemsResponseType: {
		defaultValue: 'Response Type',
		description: 'The response type column heading for the exam items page.',
		displayPath: `${dpRoot}|Response Type`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.ResponseType',
	},
	SPCandidateExamItemsResponse: {
		defaultValue: 'Response',
		description: 'The response column heading for the exam items page.',
		displayPath: `${dpRoot}|Response`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.Response',
	},
	SPCandidateExamItemsKey: {
		defaultValue: 'Key',
		description: 'The key column heading for the exam items page.',
		displayPath: `${dpRoot}|Key`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamItems.ColumnHeading.Key',
	},
	SPCandidateExamItemsMaxPointsPossible: {
		defaultValue: 'Max Points Possible',
		description: 'Max points possible column heading for the exam items page.',
		displayPath: `${dpRoot}|Max Points Possible`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ExamMsftExamDetails,
		legacyName: 'Exam.MSFTExamDetails/Candidate.ExamItems.FieldLabel.MaxPointsPossible',
	},
	SPCandidateExamItemsCaseStudy: {
		defaultValue: 'Case Study',
		description: 'Case study column heading for the exam items page.',
		displayPath: `${dpRoot}|Case Study`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ExamMsftExamDetails,
		legacyName: 'Exam.MSFTExamDetails/Candidate.ExamItems.FieldLabel.CaseStudy',
	},
	SPCandidateExamItemsObUsed: {
		defaultValue: 'OB Used',
		description: 'OB Used column heading for the exam items page.',
		displayPath: `${dpRoot}|OB Used`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ExamMsftExamDetails,
		legacyName: 'Exam.MSFTExamDetails/Candidate.ExamItems.FieldLabel.ObUsed',
	},
	SPCandidateExamItemsObFirst: {
		defaultValue: 'OB First',
		description: 'OB First column heading for the exam items page.',
		displayPath: `${dpRoot}|OB First`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ExamMsftExamDetails,
		legacyName: 'Exam.MSFTExamDetails/Candidate.ExamItems.FieldLabel.ObFirst',
	},
	SPCandidateExamItemsObItemTime: {
		defaultValue: 'OB Item Time',
		description: 'OB Item Time column heading for the exam items page.',
		displayPath: `${dpRoot}|OB Item Time`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ExamMsftExamDetails,
		legacyName: 'Exam.MSFTExamDetails/Candidate.ExamItems.FieldLabel.ObItemTime',
	},
	SPCandidateExamItemsObPages: {
		defaultValue: 'OB Pages',
		description: 'OB Pages column heading for the exam items page.',
		displayPath: `${dpRoot}|OB Pages`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ExamMsftExamDetails,
		legacyName: 'Exam.MSFTExamDetails/Candidate.ExamItems.FieldLabel.ObPages',
	},
	SPCandidateExamItemsSummaryRowLabel: {
		defaultValue: 'Total',
		description: 'The summary row label for the table.',
		displayPath: `${dpRoot}|Summary Row Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
	},
};

export const LOC_KEYS = MapLocKeys<SPCandidateExamItemsLoc>(spCandidateExamItemsLocs);
